/**
 * The Issue Report offer and its CTA journey (FR-201 to FR-205, MYN-285,
 * MYN-286). Wire values mirror the Backend's public offer endpoint and the CMS
 * section flag; strings are the buyer-facing words.
 */

/** Public, read-only offer endpoint (FR-201). `/v1` comes from `API_BASE_URL`. */
export const ISSUE_REPORT_OFFER_ENDPOINT = '/public/issue-report-offer';

/** The offer's two states (A-233): "no active plan" is one state, not an error. */
export const ISSUE_REPORT_OFFER_STATE = {
  AVAILABLE: 'available',
  COMING_SOON: 'coming_soon',
} as const;

/** The CMS section flag that marks a CTA as the Issue Report buy action (FR-202). */
export const OFFER_CTA = {
  ISSUE_REPORT: 'issue-report',
} as const;

/** The CMS placeholders the page fills from the offer (A-230). Matched with optional inner spaces. */
export const OFFER_TOKEN = {
  PRICE: 'issueReportPrice',
  ACCESS_LENGTH: 'issueReportAccessLength',
} as const;

export const ISSUE_REPORT_OFFER_STRINGS = {
  /** The one wording for an Issue Report that cannot be bought right now (FR-203). */
  COMING_SOON: 'Coming Soon',
  /** Under the CTA for a signed-out visitor (FR-223, E13). */
  SIGN_IN_LINE: 'Log in or create an account to get started.',
} as const;

/** The pop-up a signed-out visitor sees before the journey starts (FR-223, E41). */
export const ISSUE_REPORT_PATH_STRINGS = {
  EYEBROW: 'Investor Readiness Issue Report',
  TITLE: "Here's how to get your Issue Report",
  DESCRIPTION: 'Four steps, about 5 minutes. You can stop and come back at any point.',
  STEPS: [
    {
      title: 'Log in or register',
      detail:
        "Use your company email. Personal addresses like Gmail can't be used for an Issue Report.",
    },
    {
      title: 'Verify your email and set up two-step verification',
      detail: 'A code by email, then an authenticator app on your phone.',
    },
    {
      title: 'Create your Startup Profile',
      detail: 'Your company name and website. Add the rest later.',
    },
    {
      title: 'Complete payment for the Issue Report',
      // Filled from the public offer, like the page's own price copy (FR-202).
      detail:
        '{{issueReportPrice}} for {{issueReportAccessLength}} of access, paid securely through Stripe.',
    },
  ],
  GET_STARTED: 'Get started',
  HAVE_ACCOUNT: 'I already have an account',
} as const;

/**
 * The purchase intent carried from the CTA through login, signup, verification
 * and Create Startup (FR-206, MYN-286). Only the value `issue-report` is
 * accepted; anything else is ignored. The stored value holds the intent name
 * and its expiry, never a credential.
 */
export const PURCHASE_INTENT = {
  ISSUE_REPORT: 'issue-report',
} as const;

export type PurchaseIntent = (typeof PURCHASE_INTENT)[keyof typeof PURCHASE_INTENT];

/** The URL parameter the CTA link and the login/signup links carry. */
export const PURCHASE_INTENT_PARAM = 'intent';

export const PURCHASE_INTENT_STORAGE_KEY = 'mnr.purchase-intent';

/** How long a stored intent is honoured (A-239): 7 days, best effort per browser. */
export const PURCHASE_INTENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * How the visitor entered the Issue Report journey: by registering, or by
 * logging in to an existing account. A signed-in visitor who clicks the CTA
 * counts as `login`. Decides which steps the progress bar shows (FR-224).
 */
export const JOURNEY_ROUTE = {
  REGISTER: 'register',
  LOGIN: 'login',
} as const;

export type JourneyRoute = (typeof JOURNEY_ROUTE)[keyof typeof JOURNEY_ROUTE];

/** Where a journey screen sits on the progress bar; `done` marks every step complete. */
export type IssueReportJourneyStage = 'account' | 'secure' | 'startup' | 'payment' | 'done';

/**
 * The progress bar's steps per route, and where each stage sits on them
 * (FR-224). Logging in already asks for the authenticator code, so the login
 * route has no separate "Verify and secure" step: its `secure` stage (an
 * older account setting up its app while logging in) stays on "Log in".
 * `done` sits past the last step, so every step shows complete.
 */
export const ISSUE_REPORT_JOURNEY_BAR: Record<
  JourneyRoute,
  { steps: readonly { label: string }[]; index: Record<IssueReportJourneyStage, number> }
> = {
  register: {
    steps: [
      { label: 'Register' },
      { label: 'Verify and secure' },
      { label: 'Startup Profile' },
      { label: 'Payment' },
    ],
    index: { account: 0, secure: 1, startup: 2, payment: 3, done: 4 },
  },
  login: {
    steps: [{ label: 'Log in' }, { label: 'Startup Profile' }, { label: 'Payment' }],
    index: { account: 0, secure: 0, startup: 1, payment: 2, done: 3 },
  },
};
